import React, { useRef } from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 're-resizable';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import { useLiveSessionDetail } from './hooks';
import JsonBlock from './JsonBlock';
import { formatDuration } from './tableShared';
import useSessionDetailPIP, { MIN_HEIGHT, MIN_WIDTH } from './useSessionDetailPIP';
import type {
  EndReason, HeartbeatStatus, LiveSessionDetail, Participant, RosterStudent,
} from './types';

interface SessionDetailPIPProps {
  onClose: () => void;
  sessionId: string;
}

const HEARTBEAT_LABEL: Record<HeartbeatStatus, string> = {
  abandoned: 'Abandoned',
  healthy: 'Healthy',
  missing: 'Missing',
  stale: 'Stale',
};

const HEARTBEAT_CLASS: Record<HeartbeatStatus, string> = {
  abandoned: 'tw-bg-red-100 tw-text-red-700 tw-border-red-200',
  healthy: 'tw-bg-green-100 tw-text-green-700 tw-border-green-200',
  missing: 'tw-bg-gray-100 tw-text-gray-600 tw-border-gray-200',
  stale: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-200',
};

const RESIZE_HANDLES = {
  bottom: false,
  bottomLeft: false,
  bottomRight: true,
  left: false,
  right: false,
  top: false,
  topLeft: false,
  topRight: false,
};

const PANEL_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-gray-300',
  'tw-rounded-lg tw-shadow-xl',
  'tw-flex tw-flex-col tw-h-full tw-overflow-hidden',
);

const HEADER_CLASS = cn(
  'pip-handle tw-cursor-move tw-bg-tci-purple tw-text-white',
  'tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-2 tw-select-none',
);

const HEADER_BUTTON_CLASS = cn(
  'tw-bg-transparent tw-border-0 tw-text-white tw-cursor-pointer',
  'tw-px-1 tw-text-base hover:tw-opacity-80',
);

const formatElapsed = (isoStartedAt: string): string => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(isoStartedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const HeartbeatBadge = ({ status }: { status: HeartbeatStatus }) => (
  <span
    className={cn(
      'tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium',
      'tw-border tw-border-solid',
      HEARTBEAT_CLASS[status],
    )}
  >
    {HEARTBEAT_LABEL[status]}
  </span>
);

const RosterList = ({ label, students }: { label: string; students: RosterStudent[] }) => (
  <div>
    <h4 className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 tw-mt-0 tw-mb-1">
      {label} ({students.length})
    </h4>
    {students.length === 0 ? (
      <div className="tw-text-xs tw-text-gray-400 tw-italic">No students</div>
    ) : (
      <ul className="tw-list-none tw-pl-0 tw-m-0 tw-flex tw-flex-wrap tw-gap-1">
        {students.map(s => (
          <li
            key={s.id}
            className="tw-text-xs tw-bg-gray-100 tw-rounded tw-px-2 tw-py-0.5 tw-text-gray-700"
          >
            {s.full_name}
          </li>
        ))}
      </ul>
    )}
  </div>
);

interface HeaderProps {
  isLoading: boolean;
  minimized: boolean;
  onClose: () => void;
  onToggleMinimize: () => void;
  sessionId: string;
  subtitle: string;
}

const Header = ({ isLoading, minimized, onClose, onToggleMinimize, sessionId, subtitle }: HeaderProps) => (
  <div className={HEADER_CLASS}>
    <span aria-hidden className="tw-text-xs tw-opacity-70">⋮⋮</span>
    <span className="tw-flex-1 tw-text-sm tw-font-medium tw-truncate" title={subtitle}>
      {isLoading ? 'Loading…' : subtitle}
    </span>
    <a
      aria-label="Open session forensics"
      className={cn(HEADER_BUTTON_CLASS, 'tw-text-xs tw-underline tw-no-underline hover:tw-underline')}
      href={Routes.admin_live_session_path(sessionId)}
      rel="noreferrer"
      target="_blank"
    >
      Forensics
    </a>
    <button
      aria-label={minimized ? 'Expand panel' : 'Minimize panel'}
      className={HEADER_BUTTON_CLASS}
      type="button"
      onClick={onToggleMinimize}
    >
      {minimized ? '▢' : '–'}
    </button>
    <button
      aria-label="Close panel"
      className={HEADER_BUTTON_CLASS}
      type="button"
      onClick={onClose}
    >
      ×
    </button>
  </div>
);

const LoadingBody = () => (
  <div className="tw-space-y-3">
    <div className="tw-h-4 tw-bg-gray-100 tw-rounded tw-animate-pulse tw-w-3/4" />
    <div className="tw-h-4 tw-bg-gray-100 tw-rounded tw-animate-pulse tw-w-1/2" />
    <div className="tw-h-20 tw-bg-gray-100 tw-rounded tw-animate-pulse" />
  </div>
);

const END_REASON_LABEL: Record<EndReason, string> = {
  error: 'Error',
  teacher_ended: 'Teacher ended',
  timeout: 'Timeout',
};

const END_REASON_CLASS: Record<EndReason, string> = {
  error: 'tw-bg-red-100 tw-text-red-700 tw-border-red-200',
  teacher_ended: 'tw-bg-gray-100 tw-text-gray-700 tw-border-gray-200',
  timeout: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-200',
};

const EndReasonBadge = ({ reason }: { reason: EndReason | null }) => {
  if (!reason) return null;
  return (
    <span
      className={cn(
        'tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium',
        'tw-border tw-border-solid',
        END_REASON_CLASS[reason],
      )}
    >
      {END_REASON_LABEL[reason]}
    </span>
  );
};

const ParticipantsList = ({ participants }: { participants: Participant[] }) => (
  <div>
    <h4 className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 tw-mt-0 tw-mb-1">
      Participants ({participants.length})
    </h4>
    {participants.length === 0 ? (
      <div className="tw-text-xs tw-text-gray-400 tw-italic">No one joined</div>
    ) : (
      <ul className="tw-list-none tw-pl-0 tw-m-0 tw-flex tw-flex-col tw-gap-1">
        {participants.map(p => (
          <li
            key={p.id}
            className={cn(
              'tw-text-xs tw-bg-gray-100 tw-rounded tw-px-2 tw-py-1 tw-text-gray-700',
              'tw-flex tw-justify-between tw-gap-2',
            )}
          >
            <span>{p.full_name}</span>
            <span className="tw-text-gray-500">joined {formatElapsed(p.first_joined_at)} ago</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ActiveDetailBody = ({ session }: { session: LiveSessionDetail }) => (
  <>
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <div className="tw-text-xs tw-text-gray-500">
        Started {formatElapsed(session.started_at)} ago
        {session.current_slide && <> · Slide {session.current_slide}</>}
      </div>
      <HeartbeatBadge status={session.heartbeat_status} />
    </div>

    <div className="tw-bg-gray-50 tw-rounded tw-p-3 tw-text-sm">
      <div className="tw-text-gray-700">
        <strong>{session.connected_student_count}</strong>
        {' of '}
        {session.total_student_count} students connected
      </div>
      <div className="tw-text-gray-500 tw-text-xs tw-mt-1">
        {session.program.name} · {session.slide_show.name}
      </div>
    </div>

    {session.roster && (
      <>
        <RosterList label="Connected" students={session.roster.connected} />
        <RosterList label="Not yet joined" students={session.roster.not_yet_joined} />
      </>
    )}

    {session.raw_state && (
      <details className="tw-text-xs tw-text-gray-600">
        <summary className="tw-cursor-pointer tw-text-gray-500 tw-uppercase tw-tracking-wide tw-font-semibold">
          Raw state
        </summary>
        <JsonBlock className="tw-mt-2" value={session.raw_state} />
      </details>
    )}
  </>
);

const EndedDetailBody = ({ session }: { session: LiveSessionDetail }) => (
  <>
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <div className="tw-text-xs tw-text-gray-500">
        Ended {session.ended_at ? `${formatElapsed(session.ended_at)} ago` : '—'}
      </div>
      <EndReasonBadge reason={session.end_reason} />
    </div>

    <div className="tw-bg-gray-50 tw-rounded tw-p-3 tw-text-sm">
      <div className="tw-text-gray-700">
        Duration: <strong>{formatDuration(session.duration_seconds)}</strong>
      </div>
      <div className="tw-text-gray-500 tw-text-xs tw-mt-1">
        {session.program.name} · {session.slide_show.name}
      </div>
    </div>

    <ParticipantsList participants={session.participants ?? []} />
  </>
);

const DetailBody = ({ session }: { session: LiveSessionDetail }) => (
  session.status === 'ended'
    ? <EndedDetailBody session={session} />
    : <ActiveDetailBody session={session} />
);

const SessionDetailPIP = ({ onClose, sessionId }: SessionDetailPIPProps) => {
  const { data, isLoading } = useLiveSessionDetail(sessionId);
  const { setPosition, setSize, state, toggleMinimized } = useSessionDetailPIP();
  const nodeRef = useRef<HTMLDivElement>(null);

  const subtitle = data
    ? `${data.teacher.name ?? 'Unknown'} • ${data.slide_show.name ?? '—'}`
    : 'Loading…';

  const size = {
    height: state.minimized ? 'auto' : state.height,
    width: state.width,
  };

  return (
    <Draggable
      bounds="body"
      handle=".pip-handle"
      nodeRef={nodeRef}
      position={{ x: state.x, y: state.y }}
      onStop={(_e, d) => setPosition(d.x, d.y)}
    >
      <div
        ref={nodeRef}
        aria-label="Session detail panel"
        className="tw-fixed tw-top-0 tw-left-0 tw-z-50"
        role="dialog"
      >
        <Resizable
          enable={state.minimized ? {} : RESIZE_HANDLES}
          handleClasses={{ bottomRight: 'pip-resize-handle' }}
          handleStyles={{ bottomRight: { cursor: 'nwse-resize', height: 16, width: 16 } }}
          minHeight={MIN_HEIGHT}
          minWidth={MIN_WIDTH}
          size={size}
          onResizeStop={(_e, _dir, ref) => setSize(ref.offsetWidth, ref.offsetHeight)}
        >
          <div className={PANEL_CLASS}>
            <Header
              isLoading={isLoading}
              minimized={state.minimized}
              sessionId={sessionId}
              subtitle={subtitle}
              onClose={onClose}
              onToggleMinimize={toggleMinimized}
            />
            {!state.minimized && (
              <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-flex tw-flex-col tw-gap-4">
                {isLoading || !data ? <LoadingBody /> : <DetailBody session={data} />}
              </div>
            )}
          </div>
        </Resizable>
      </div>
    </Draggable>
  );
};

export default SessionDetailPIP;
